import clsx from "clsx";
import { type ComponentProps, useCallback, useMemo, useState } from "react";

import { Navigation } from "@features/article-navigation";
import { DocumentForm } from "@features/document-form";

import { StickyCard } from "@entities/sticky-card";

import type { DocumentFormBlock, DynamicZone } from "@shared/content-types";
import type { StrapiMedia } from "@shared/content-types/schemas/media.schema";
import type { GlobalType } from "@shared/content-types/types";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Container } from "@shared/ui/container";
import {
  type BlockWithIdAndComponent,
  DynamicZoneRenderer,
} from "@shared/ui/dynamic-zone-renderer";
import { Image } from "@shared/ui/image";
import { Popup } from "@shared/ui/popup";
import { SharedSocial } from "@shared/ui/social-shared";

import { articleBlockSchema } from "../model/section-list";
import { ArticleContext } from "./article-context";

import s from "./article-content.module.scss";

export type ArticleContentProps = ComponentProps<"div"> & {
  articleBlocks: DynamicZone;
  className?: string;
  showSocialShare?: boolean;
  mainImage?: StrapiMedia;
  formSettings?: GlobalType["feedbackForm"] | null;
};

export const ArticleContent = (props: ArticleContentProps) => {
  const {
    articleBlocks,
    mainImage,
    className,
    showSocialShare = true,
    formSettings,
  } = props;

  const [activeSection, setSection] = useState("");

  const sections = useMemo(() => {
    return articleBlocks
      .filter(
        (
          block,
        ): block is {
          id: number;
          __component: "article.section-name";
          name: string;
        } => block.__component === "article.section-name",
      )
      .map((block) => block.name);
  }, [articleBlocks]);

  const documentForm = useMemo(
    () =>
      articleBlocks.find(
        (block): block is DocumentFormBlock =>
          block.__component === "ui.document-form",
      ),
    [articleBlocks],
  );

  const articleValue = useMemo(() => {
    return { activeSection, setSection };
  }, [activeSection]);

  const [isOpen, setOpen] = useState(false);

  const handleOpenNavigation = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const validBlocks = articleBlocks.filter(
    (block): block is Exclude<typeof block, { id: undefined }> =>
      block.id !== undefined,
  ) as BlockWithIdAndComponent[];

  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleFormClose = () => {
    setIsFormOpen(false);
  };

  const handleFormOpen = () => {
    setIsFormOpen(true);
  };

  return (
    <ArticleContext.Provider value={articleValue}>
      <Container
        className={clsx(
          s.root,
          { [s.flexContent]: sections.length <= 1 },
          className,
        )}
      >
        <div className={s.wrapper}>
          {mainImage?.url && (
            <Image
              src={mainImage.url}
              alt={mainImage?.alternativeText ?? "service image"}
              className={s.image}
              height="100%"
              objectFit="cover"
            />
          )}
          <div className={s.content}>
            <DynamicZoneRenderer
              blocksData={validBlocks}
              blocksSchema={articleBlockSchema}
            />

            {documentForm && (
              <ButtonWithIcon
                iconName="plus"
                iconFillColor="gold"
                iconStrokeColor="white"
                color="azure"
                variant="primary-dark"
                className={s.moreBtn}
                onClick={handleFormOpen}
              >
                {documentForm.button?.text ?? "Скачать гайд бесплатно"}
              </ButtonWithIcon>
            )}
          </div>
        </div>
        {sections.length > 1 && (
          <StickyCard isOpen={isOpen}>
            <Navigation
              sections={sections}
              currentSection={activeSection}
              onOpenNavigation={handleOpenNavigation}
            />
          </StickyCard>
        )}
        {showSocialShare && (
          <SharedSocial
            className={clsx(s.socialShare, {
              [s.viewShare]: sections.length <= 1,
            })}
          />
        )}
      </Container>
      {documentForm && (
        <Popup
          id="document-popup"
          isOpen={isFormOpen}
          onClose={handleFormClose}
          variant="feedback"
        >
          <DocumentForm
            title={documentForm.title}
            text={documentForm.text}
            document={formSettings?.privacyPolicy}
            personal={formSettings?.privacyPolicyAlt}
            documentFile={documentForm.document}
            isOpen={isFormOpen}
            onSuccess={handleFormClose}
            className={s.documentForm}
          />
        </Popup>
      )}
    </ArticleContext.Provider>
  );
};

ArticleContent.displayName = "ArticleContent";
